import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import Seo from "../seo";
import { api } from "../api";
import { Reveal, Breadcrumbs } from "../components/motion";

export default function TreatmentDetail() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setData(null); setNotFound(false);
        api.get(`/content/treatments/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) return (
        <div className="mx-auto max-w-3xl px-5 py-24 text-center">
            <h1 className="font-display text-3xl text-navy">Treatment not found</h1>
            <Link to="/treatments" data-testid="back-to-treatments-btn" className="mt-6 inline-block text-accent font-semibold">Back to all treatments</Link>
        </div>
    );
    if (!data) return <div className="mx-auto max-w-7xl px-5 py-24 text-steel">Loading…</div>;

    const t = data.treatment;
    const sections = [
        ["What is it?", t.what],
        ["When is treatment considered?", t.when],
        ["Available treatment options", t.options],
        ["What does the procedure involve?", t.involves],
        ["Expected recovery", t.recovery],
        ["Common risks", t.risks],
        ["When to seek medical attention", t.seek_attention],
    ];

    return (
        <div className="mx-auto max-w-5xl px-5 py-14 lg:px-10 lg:py-20" data-testid="treatment-detail">
            <Seo title={`${t.name} | Dr. Krishnakanth AVB, Urologist Madurai`} description={t.summary}
                jsonLd={{
                    "@context": "https://schema.org", "@type": "MedicalWebPage",
                    "name": t.name, "description": t.summary,
                    "about": { "@type": "MedicalProcedure", "name": t.name },
                    "author": { "@type": "Physician", "name": "Dr. Krishnakanth AVB" },
                }} />
            <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Treatments", to: "/treatments" }, { label: t.name }]} />
            <Reveal>
                <p className="mt-10 font-mono-label text-accent">{t.category}</p>
                <h1 className="mt-4 font-display text-4xl font-medium leading-tight text-navy sm:text-5xl">{t.name}</h1>
                <p className="mt-6 max-w-3xl text-base leading-relaxed text-steel md:text-lg">{t.summary}</p>
            </Reveal>

            <div className="mt-14 grid gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:gap-16">
                <div>
                    {sections.map(([title, body], i) => (
                        <Reveal key={title} className="border-t border-border py-8 first:border-t-0 first:pt-0">
                            <h2 className="flex items-baseline gap-4 font-display text-xl font-medium text-navy">
                                <span className="font-mono-label text-accent text-xs">{String(i + 1).padStart(2, "0")}</span>
                                {title}
                            </h2>
                            <p className="mt-4 text-base leading-relaxed text-steel">{body}</p>
                        </Reveal>
                    ))}
                    <Reveal className="border-t border-border py-8">
                        <h2 className="flex items-baseline gap-4 font-display text-xl font-medium text-navy">
                            <span className="font-mono-label text-accent text-xs">08</span>
                            Frequently asked questions
                        </h2>
                        <Accordion type="single" collapsible className="mt-4" data-testid="treatment-faq">
                            {t.faq.map((f, i) => (
                                <AccordionItem key={i} value={`faq-${i}`}>
                                    <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-base font-medium text-navy">
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-base leading-relaxed text-steel">
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>
                </div>
                <aside>
                    <Reveal delay={0.1}>
                        <div className="sticky top-24 rounded-2xl border border-border bg-mist p-7">
                            <h3 className="font-display text-lg font-medium text-navy">Is this procedure right for you?</h3>
                            <p className="mt-3 text-sm leading-relaxed text-steel">
                                The right treatment depends on your specific findings and overall health. A consultation
                                includes a clear, unhurried discussion of options.
                            </p>
                            <Link to="/appointment" data-testid="treatment-book-appointment-btn"
                                className="mt-6 flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-accent">
                                <CalendarCheck className="h-4 w-4" aria-hidden="true" />
                                Book an Appointment
                            </Link>
                        </div>
                    </Reveal>
                </aside>
            </div>
            <p className="mt-16 border-t border-border pt-6 text-xs leading-relaxed text-slate-400">{data.disclaimer}</p>
        </div>
    );
}
